import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Alert,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ImageList,
  ImageListItem,
  Tooltip,
  TablePagination,
  LinearProgress
} from '@mui/material';
import {
  Add as AddIcon,
  ArrowBack as ArrowBackIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Close as CloseIcon,
  Search as SearchIcon,
  FilterList as FilterListIcon,
  Camera as CameraIcon,
  PhotoCamera as PhotoCameraIcon
} from '@mui/icons-material';
import axios from 'axios';
import { API_BASE_URL } from '../config';
import imageCompression from 'browser-image-compression';

function Assets() {
  const location = useLocation();
  const navigate = useNavigate();
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [openDetailsDialog, setOpenDetailsDialog] = useState(false);
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [zones, setZones] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    fa_number: '',
    barcode: '',
    serial_number: '',
    supplier: '',
    unit: '',
    category: '',
    description: '',
    quantity: 0,
    images: [],
    company_id: '',
    zone_id: '',
    condition: ''
  });
  const [selectedImages, setSelectedImages] = useState([]);
  const [imagePreviews, setImagePreviews] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [localFilteredAssets, setLocalFilteredAssets] = useState([]);
  const [allAssets, setAllAssets] = useState([]);
  const [filteredAssets, setFilteredAssets] = useState([]);
  const [selectedZoneFilter, setSelectedZoneFilter] = useState('');
  const [selectedCompanyFilter, setSelectedCompanyFilter] = useState('');
  const [selectedLocationTypeFilter, setSelectedLocationTypeFilter] = useState('');
  const [locationTypes, setLocationTypes] = useState([]);
  const user = JSON.parse(localStorage.getItem('user'));
  const isAdmin = user?.role === 'admin';

  const { zone, company, viewOnly } = location.state || {};

  // Update pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  const [uploadProgress, setUploadProgress] = useState({});
  const [compressing, setCompressing] = useState(false);

  // Add compression options
  const compressionOptions = {
    maxSizeMB: 1,
    maxWidthOrHeight: 1920,
    useWebWorker: true
  };

  // Add debounce for search
  const searchTimeoutRef = useRef(null);

  const compressImage = async (file) => {
    try {
      const compressedFile = await imageCompression(file, compressionOptions);
      return compressedFile;
    } catch (error) {
      console.error('Error compressing image:', error);
      return file; // Return original file if compression fails
    }
  };

  useEffect(() => {
    const initializeData = async () => {
      try {
        setLoading(true);
        await Promise.all([
          fetchCompaniesIfNeeded(),
          fetchZonesIfNeeded(),
          fetchAssets()
        ]);
      } catch (err) {
        console.error('Error initializing data:', err);
        setError('Failed to load data. Please check your connection and try again.');
      } finally {
        setLoading(false);
      }
    };

    initializeData();
  }, []);

  const fetchCompaniesIfNeeded = async () => {
    const now = Date.now();
    const response = await axios.get(`${API_BASE_URL}/companies/read.php`);
    if (response.data && response.data.records) {
      setCompanies(response.data.records);
    }
  };

  const fetchZonesIfNeeded = async () => {
    const response = await axios.get(`${API_BASE_URL}/zones/read.php`);
    if (response.data && response.data.records) {
      setZones(response.data.records);
    }
  };

  const fetchAssets = async (pageToFetch = null, rowsPerPageToFetch = null) => {
    try {
      setLoading(true);
      const currentPage = pageToFetch !== null ? pageToFetch : page;
      const currentRowsPerPage = rowsPerPageToFetch !== null ? rowsPerPageToFetch : rowsPerPage;
      
      const params = new URLSearchParams({
        page: currentPage + 1, // Backend uses 1-based pagination
        limit: currentRowsPerPage,
        ...(searchTerm && searchTerm.trim() && { search: searchTerm.trim() }),
        ...(selectedCompanyFilter && { company_id: selectedCompanyFilter }),
        ...(selectedZoneFilter && { zone_id: selectedZoneFilter }),
        ...(selectedLocationTypeFilter && { location_type: selectedLocationTypeFilter }),
        _t: Date.now() // Prevent caching
      });

      // Always prioritize zone from location state if available
      if (zone?.id) {
        params.set('zone_id', zone.id);
      }

      console.log('Fetching assets with params:', params.toString()); // Debug log

      const response = await axios.get(`${API_BASE_URL}/assets/read.php?${params}`);
      
      if (response.data?.status === 'success' && response.data?.data) {
        const { records, pagination } = response.data.data;
        
        console.log('Received assets:', records); // Debug log
        
        // Update assets and total count
        setAssets(records.map(asset => ({
          ...asset,
          images: Array.isArray(asset.images) ? asset.images : []
        })));
        setTotalCount(pagination?.total || 0);
        
        // Update location types
        const uniqueLocationTypes = [...new Set(records.map(asset => asset.location_type))].filter(Boolean);
        setLocationTypes(uniqueLocationTypes);

        // If we're on a page that's now empty, go to the last available page
        if (records.length === 0 && currentPage > 0) {
          const lastPage = Math.ceil((pagination?.total || 0) / currentRowsPerPage) - 1;
          if (lastPage >= 0) {
            setPage(lastPage);
            // Fetch the last page
            params.set('page', lastPage + 1);
            const lastPageResponse = await axios.get(`${API_BASE_URL}/assets/read.php?${params}`);
            if (lastPageResponse.data?.status === 'success' && lastPageResponse.data?.data) {
              setAssets(lastPageResponse.data.data.records.map(asset => ({
                ...asset,
                images: Array.isArray(asset.images) ? asset.images : []
              })));
            }
          }
        }
      } else {
        throw new Error(response.data?.message || 'Invalid response format from server');
      }
    } catch (err) {
      console.error('Error fetching assets:', err);
      setError(err.response?.data?.message || err.message || 'Error fetching assets');
      // Clear assets on error to prevent showing stale data
      setAssets([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (asset = null) => {
    if (zone && zone.status === 'closed') {
      setError('Cannot add assets to a closed zone');
      return;
    }
    if (asset) {
      setIsEditing(true);
      setSelectedAsset(asset);
      // Ensure all fields are properly initialized
      setFormData({
        name: asset.name || '',
        fa_number: asset.fa_number || '',
        barcode: asset.barcode || '',
        serial_number: asset.serial_number || '',
        supplier: asset.supplier || '',
        unit: asset.unit || '',
        category: asset.category || '',
        description: asset.description || '',
        quantity: parseInt(asset.quantity) || 0,
        company_id: asset.company_id || '',
        zone_id: asset.zone_id || '',
        condition: asset.condition || ''
      });
      setSelectedImages([]);
      // Set existing images as previews
      if (asset.images && asset.images.length > 0) {
        const existingPreviews = asset.images.map(image => ({
          preview: `${API_BASE_URL.replace('/api', '')}/uploads/assets/${image}`,
          isExisting: true,
          name: image
        }));
        setImagePreviews(existingPreviews);
      } else {
        setImagePreviews([]);
      }
    } else {
      setIsEditing(false);
      setSelectedAsset(null);
      setFormData({
        name: '',
        fa_number: '',
        barcode: '',
        serial_number: '',
        supplier: '',
        unit: '',
        category: '',
        description: '',
        quantity: 0,
        images: [],
        company_id: company?.id || '',
        zone_id: zone?.id || '',
        condition: ''
      });
      setSelectedImages([]);
      setImagePreviews([]);
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setIsEditing(false);
    setFormData({
      name: '',
      fa_number: '',
      barcode: '',
      serial_number: '',
      supplier: '',
      unit: '',
      category: '',
      description: '',
      quantity: 0,
      images: [],
      company_id: company?.id || '',
      zone_id: zone?.id || '',
      condition: ''
    });
    setSelectedImages([]);
    setImagePreviews([]);
  };

  const handleOpenDetailsDialog = (asset) => {
    setSelectedAsset(asset);
    setOpenDetailsDialog(true);
  };

  const handleCloseDetailsDialog = () => {
    setSelectedAsset(null);
    setOpenDetailsDialog(false);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === 'quantity') {
      // Ensure quantity is a non-negative integer
      const intValue = parseInt(value) || 0;
      setFormData(prev => ({
        ...prev,
        [name]: Math.max(0, intValue)
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value,
        // Reset zone_id when company changes
        ...(name === 'company_id' && { zone_id: '' })
      }));
    }
  };

  const handleImageChange = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length + selectedImages.length > 5) {
      setError('Maximum 5 images allowed');
      return;
    }

    setCompressing(true);
    try {
      const compressedFiles = await Promise.all(
        files.map(async (file) => {
          // Check if the file is an image
          if (!file.type.startsWith('image/')) {
            setError('Please select only image files');
            return null;
          }

          // Compress the image
          const compressedFile = await compressImage(file);
          
          // Create a preview URL
          const preview = URL.createObjectURL(compressedFile);
          return {
            file: compressedFile,
            preview
          };
        })
      );

      const validPreviews = compressedFiles.filter(preview => preview !== null);
      if (validPreviews.length > 0) {
        setSelectedImages(prev => [...prev, ...validPreviews.map(p => p.file)]);
        setImagePreviews(prev => [...prev, ...validPreviews]);
      }
    } catch (err) {
      console.error('Error processing images:', err);
      setError('Error processing images. Please try again.');
    } finally {
      setCompressing(false);
    }
  };

  const removeImage = (index) => {
    const preview = imagePreviews[index];
    // If it's an existing image, we need to track it for removal
    if (preview.isExisting) {
      // Add to a list of images to be removed
      setFormData(prev => ({
        ...prev,
        imagesToRemove: [...(prev.imagesToRemove || []), preview.name]
      }));
    } else {
      // For new images, revoke the object URL
      if (preview.preview) {
        URL.revokeObjectURL(preview.preview);
      }
      // Remove from selectedImages if it's a new upload
      setSelectedImages(prev => prev.filter((_, i) => i !== index));
    }
    // Remove from previews
    setImagePreviews(prev => prev.filter((_, i) => i !== index));
  };

  // Clean up object URLs when component unmounts
  useEffect(() => {
    return () => {
      imagePreviews.forEach(preview => {
        if (preview?.preview) {
          URL.revokeObjectURL(preview.preview);
        }
      });
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (zone && zone.status === 'closed') {
      setError('Cannot modify assets in a closed zone');
      return;
    }

    let formDataToSend;
    try {
      setLoading(true);
      setError(null);
      formDataToSend = new FormData();
      
      // Add all form fields
      Object.keys(formData).forEach(key => {
        if (formData[key] !== null && formData[key] !== undefined) {
          // Ensure quantity is sent as a number
          if (key === 'quantity') {
            formDataToSend.append(key, parseInt(formData[key]) || 0);
          } else if (key === 'imagesToRemove') {
            // Add images to remove as a JSON string
            formDataToSend.append(key, JSON.stringify(formData[key]));
          } else {
            formDataToSend.append(key, formData[key]);
          }
        }
      });

      // Add asset ID for updates
      if (isEditing && selectedAsset) {
        formDataToSend.append('id', selectedAsset.id);
      }

      // Add new images
      if (selectedImages && selectedImages.length > 0) {
        console.log('Selected images for upload:', selectedImages);
        selectedImages.forEach((image, index) => {
          if (image instanceof File || image instanceof Blob) {
            const fileToUpload = image instanceof Blob && !(image instanceof File) 
              ? new File([image], image.name || `image_${index}.png`, { type: image.type })
              : image;

            console.log(`Appending image ${index}:`, { 
              name: fileToUpload.name, 
              type: fileToUpload.type, 
              size: fileToUpload.size,
              lastModified: fileToUpload.lastModified
            });
            formDataToSend.append('images[]', fileToUpload);
          }
        });
      }

      const endpoint = isEditing ? 'update.php' : 'create.php';

      // Log the form data for debugging
      const formDataObj = {};
      for (let [key, value] of formDataToSend.entries()) {
        if (value instanceof File) {
          formDataObj[key] = {
            name: value.name,
            type: value.type,
            size: value.size,
            lastModified: value.lastModified
          };
        } else {
          formDataObj[key] = value;
        }
      }
      console.log('Form data being sent:', formDataObj);

      // Make the request with proper headers and timeout
      const response = await axios({
        method: 'post',
        url: `${API_BASE_URL}/assets/${endpoint}`,
        data: formDataToSend,
        headers: { 
          'Content-Type': 'multipart/form-data',
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          console.log('Upload progress:', percentCompleted + '%');
        },
        timeout: 30000, // 30 second timeout
        maxContentLength: Infinity,
        maxBodyLength: Infinity
      });

      console.log('Server response:', response.data); // Debug log

      if (response.data) {
        setSuccess(isEditing ? 'Asset updated successfully' : 'Asset created successfully');
        handleCloseDialog();
        
        // Get the updated asset data from the response
        const updatedAssetData = response.data.data?.asset || response.data.asset || {};
        console.log('Updated asset data:', updatedAssetData); // Debug log
        
        // Process uploaded images from the response
        const uploadedImages = response.data.data?.images || response.data.images || [];
        console.log('Uploaded images from response:', uploadedImages); // Debug log
        
        // Get existing images from the current asset if updating
        const existingImages = isEditing && selectedAsset?.images ? selectedAsset.images : [];
        console.log('Existing images:', existingImages); // Debug log
        
        // Process new image paths
        const newImagePaths = uploadedImages.map(img => {
          if (typeof img === 'string') return img;
          return img.saved_name || img.original_name || img;
        });
        console.log('New image paths:', newImagePaths); // Debug log
        
        // Combine existing images with new ones for updates
        const finalImages = isEditing 
          ? [...existingImages, ...newImagePaths].filter((v, i, a) => a.indexOf(v) === i) // Remove duplicates
          : newImagePaths;
        
        console.log('Final images array:', finalImages); // Debug log

        // Update the assets list with the new data
        setAssets(prevAssets => {
          if (isEditing && selectedAsset) {
            // For updates, replace the existing asset
            const updatedAssets = prevAssets.map(asset => 
              asset.id === selectedAsset.id 
                ? {
                    ...asset,
                    ...updatedAssetData,
                    images: finalImages
                  }
                : asset
            );
            console.log('Updated assets list:', updatedAssets); // Debug log
            return updatedAssets;
          } else {
            // For new assets, add to the beginning of the list
            const newAsset = {
              ...updatedAssetData,
              images: finalImages
            };
            console.log('New asset:', newAsset); // Debug log
            return [newAsset, ...prevAssets];
          }
        });

        // Refresh the full list to ensure consistency
        await fetchAssets(page, rowsPerPage);
      }
    } catch (err) {
      console.error('Error details:', err);
      let errorMessage = err.response?.data?.message || err.message || `Error ${isEditing ? 'updating' : 'creating'} asset`;
      
      // Add more specific error messages for common issues
      if (err.code === 'ECONNABORTED') {
        errorMessage = 'Request timed out. Please try again.';
      } else if (err.message === 'Network Error') {
        errorMessage = 'Network connection error. Please check your internet connection and try again.';
      }
      
      setError(errorMessage);
      
      // Log detailed error information
      if (formDataToSend) {
        const formDataObj = {};
        for (let [key, value] of formDataToSend.entries()) {
          if (value instanceof File) {
            formDataObj[key] = {
              name: value.name,
              type: value.type,
              size: value.size
            };
          } else {
            formDataObj[key] = value;
          }
        }
        console.error('Asset operation failed:', {
          isEditing,
          assetId: selectedAsset?.id,
          error: err.response?.data || err.message,
          formData: formDataObj,
          errorCode: err.code,
          errorStatus: err.response?.status,
          responseData: err.response?.data
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (assetId) => {
    if (!window.confirm('Are you sure you want to delete this asset?')) {
      return;
    }

    try {
      setLoading(true);
      const response = await axios.delete(`${API_BASE_URL}/assets/delete.php`, {
        data: { id: assetId }
      });

      if (response.data?.status === 'success') {
        setSuccess('Asset deleted successfully');
        // Remove the deleted asset from the current list immediately
        setAssets(prevAssets => prevAssets.filter(asset => asset.id !== assetId));
        // Update total count
        setTotalCount(prevCount => Math.max(0, prevCount - 1));
        
        // If we're on the last page and it becomes empty, go to previous page
        if (assets.length === 1 && page > 0) {
          setPage(prevPage => prevPage - 1);
        }
        
        // Then fetch fresh data to ensure everything is in sync
        await fetchAssets();
      } else {
        throw new Error(response.data?.message || 'Failed to delete asset');
      }
    } catch (err) {
      console.error('Delete error:', err);
      setError(err.response?.data?.message || 'Error deleting asset');
    } finally {
      setLoading(false);
    }
  };

  const getZoneInfo = (zoneId) => {
    const zone = zones.find(z => z.id === zoneId);
    return zone ? {
      name: zone.name,
      status: zone.status,
      company: companies.find(c => c.id === zone.company_id)?.name
    } : null;
  };

  const getCompanyName = (companyId) => {
    const company = companies.find(c => c.id === companyId);
    return company ? company.name : 'Unknown Company';
  };

  const handleSearch = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    setPage(0); // Reset to first page on search

    // Clear any existing timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Set a shorter timeout for search (300ms instead of 1000ms)
    searchTimeoutRef.current = setTimeout(() => {
      fetchAssets(0); // Fetch first page with new search term
    }, 300);
  };

  const handleZoneFilter = (e) => {
    const zoneId = e.target.value;
    setSelectedZoneFilter(zoneId);
    setPage(0); // Reset to first page
    setAssets([]); // Clear current assets while loading
    fetchAssets(0); // Fetch first page with new filter
  };

  const handleCompanyFilter = (e) => {
    const companyId = e.target.value;
    setSelectedCompanyFilter(companyId);
    setSelectedZoneFilter('');
    setPage(0);
    fetchAssets(0); // Fetch first page with new filter
  };

  const handleLocationTypeFilter = (e) => {
    const locationType = e.target.value;
    setSelectedLocationTypeFilter(locationType);
    setPage(0);
    fetchAssets(0); // Fetch first page with new filter
  };

  // Update pagination handlers
  const handleChangePage = async (event, newPage) => {
    setPage(newPage);
    // Fetch data for the new page
    await fetchAssets(newPage);
  };

  const handleChangeRowsPerPage = async (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0); // Reset to first page when changing rows per page
    // Fetch data with new rows per page
    await fetchAssets(0, newRowsPerPage);
  };

  // Add effect to handle zone changes and ensure proper data fetching
  useEffect(() => {
    if (zone?.id) {
      setSelectedZoneFilter(zone.id);
      setPage(0); // Reset to first page when zone changes
      fetchAssets();
    }
  }, [zone?.id]);

  // Update useEffect to handle pagination changes
  useEffect(() => {
    fetchAssets();
  }, [page, rowsPerPage]); // Add pagination dependencies

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Box sx={{ paddingTop: '64px' }}>
      <Container maxWidth="lg" sx={{ mt: 2, mb: 4 }}>
        <Paper elevation={3} sx={{ p: { xs: 2, sm: 3 } }}>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, mb: 3, gap: { xs: 2, sm: 0 } }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              {(zone || company) && (
                <Button
                  startIcon={<ArrowBackIcon />}
                  onClick={() => navigate('/zones')}
                  sx={{ mr: 2 }}
                >
                  Back to Zones
                </Button>
              )}
              <Typography variant="h4" component="h1" sx={{ fontSize: { xs: '1.5rem', sm: '2rem' } }}>
                Assets {zone && `- ${zone.name}`} {company && `(${company.name})`}
              </Typography>
            </Box>
            {zone && zone.status === 'active' && (
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={() => handleOpenDialog()}
                fullWidth={false}
              >
                Add Asset
              </Button>
            )}
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {success}
            </Alert>
          )}

          <Box sx={{ mb: 3 }}>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Search assets..."
              value={searchTerm}
              onChange={handleSearch}
              InputProps={{
                startAdornment: (
                  <SearchIcon sx={{ color: 'text.secondary', mr: 1 }} />
                ),
              }}
            />
          </Box>

          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, mb: 3 }}>
            <FormControl sx={{ minWidth: { xs: '100%', sm: 200 } }}>
              <InputLabel>Filter by Company</InputLabel>
              <Select
                value={selectedCompanyFilter}
                onChange={handleCompanyFilter}
                label="Filter by Company"
                disabled={!!zone || loading}
              >
                <MenuItem value="">All Companies</MenuItem>
                {companies.map((company) => (
                  <MenuItem key={company.id} value={company.id}>
                    {company.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl sx={{ minWidth: { xs: '100%', sm: 200 } }}>
              <InputLabel>Filter by Zone</InputLabel>
              <Select
                value={selectedZoneFilter}
                onChange={handleZoneFilter}
                label="Filter by Zone"
                disabled={!!zone || loading}
              >
                <MenuItem value="">All Zones</MenuItem>
                {zones
                  .filter(zone => !selectedCompanyFilter || zone.company_id === selectedCompanyFilter)
                  .map((zone) => (
                    <MenuItem key={zone.id} value={zone.id}>
                      {zone.name} - {getCompanyName(zone.company_id)}
                    </MenuItem>
                  ))}
              </Select>
              {loading && (
                <CircularProgress
                  size={20}
                  sx={{
                    position: 'absolute',
                    right: 30,
                    top: '50%',
                    transform: 'translateY(-50%)'
                  }}
                />
              )}
            </FormControl>

            <FormControl sx={{ minWidth: { xs: '100%', sm: 200 } }}>
              <InputLabel>Filter by Location Type</InputLabel>
              <Select
                value={selectedLocationTypeFilter}
                onChange={handleLocationTypeFilter}
                label="Filter by Location Type"
                disabled={loading}
              >
                <MenuItem value="">All Location Types</MenuItem>
                {locationTypes.map((type) => (
                  <MenuItem key={type} value={type}>
                    {type}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {!viewOnly && zone?.status === 'active' && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" color="text.secondary">
                {assets.length} {assets.length === 1 ? 'Asset' : 'Assets'} Found
              </Typography>
            </Box>
          )}

          {/* Desktop View */}
          <TableContainer component={Paper} sx={{ display: { xs: 'none', md: 'block' } }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Image</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>FA Number</TableCell>
                  <TableCell>Serial Number</TableCell>
                  <TableCell>Barcode</TableCell>
                  <TableCell>Zone</TableCell>
                  <TableCell>Company</TableCell>
                  <TableCell>Location Type</TableCell>
                  <TableCell>Zone Status</TableCell>
                  {zone && zone.status === 'active' && <TableCell>Actions</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={zone ? (zone.status === 'active' ? 10 : 9) : 9} align="center">
                      <CircularProgress size={24} />
                    </TableCell>
                  </TableRow>
                ) : assets.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={zone ? (zone.status === 'active' ? 10 : 9) : 9} align="center">
                      {searchTerm || selectedZoneFilter || selectedCompanyFilter || selectedLocationTypeFilter 
                        ? 'No assets found matching your criteria' 
                        : 'No assets found'}
                    </TableCell>
                  </TableRow>
                ) : (
                  assets.map((asset) => {
                    const zoneInfo = getZoneInfo(asset.zone_id);
                    return (
                      <TableRow 
                        key={asset.id}
                        onClick={() => handleOpenDetailsDialog(asset)}
                        sx={{ cursor: 'pointer', '&:hover': { backgroundColor: 'action.hover' } }}
                      >
                        <TableCell>
                          <AssetImage
                            image={asset.images?.[0]}
                            alt={asset.name}
                            style={{ width: 50, height: 50, objectFit: 'cover', borderRadius: 4 }}
                          />
                        </TableCell>
                        <TableCell>{asset.name}</TableCell>
                        <TableCell>{asset.fa_number || 'N/A'}</TableCell>
                        <TableCell>{asset.serial_number || 'N/A'}</TableCell>
                        <TableCell>{asset.barcode || 'N/A'}</TableCell>
                        <TableCell>{zoneInfo?.name || 'N/A'}</TableCell>
                        <TableCell>{zoneInfo?.company || 'N/A'}</TableCell>
                        <TableCell>{asset.location_type || 'N/A'}</TableCell>
                        <TableCell>
                          <Chip
                            label={zoneInfo?.status === 'active' ? 'Open' : 'Closed'}
                            color={zoneInfo?.status === 'active' ? 'success' : 'error'}
                            size="small"
                          />
                        </TableCell>
                        {(!zone || (zone && zone.status === 'active')) && (
                          <TableCell>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleOpenDialog(asset);
                                }}
                              >
                                <EditIcon />
                              </IconButton>
                              {isAdmin && (
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDelete(asset.id);
                                  }}
                                >
                                  <DeleteIcon />
                                </IconButton>
                              )}
                            </Box>
                          </TableCell>
                        )}
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={totalCount}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[5, 10, 25, 50]}
              sx={{
                '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': {
                  margin: 0
                }
              }}
            />
          </TableContainer>

          {/* Mobile View */}
          <Box sx={{ display: { xs: 'block', md: 'none' } }}>
            {loading ? (
              <Box display="flex" justifyContent="center" alignItems="center" p={3}>
                <CircularProgress size={24} />
              </Box>
            ) : assets.length === 0 ? (
              <Box display="flex" justifyContent="center" alignItems="center" p={3}>
                <Typography color="text.secondary">
                  {searchTerm || selectedZoneFilter || selectedCompanyFilter || selectedLocationTypeFilter 
                    ? 'No assets found matching your criteria' 
                    : 'No assets found'}
                </Typography>
              </Box>
            ) : (
              <>
                {assets.map((asset) => {
                  const zoneInfo = getZoneInfo(asset.zone_id);
                  return (
                    <Paper
                      key={asset.id}
                      sx={{
                        p: 2,
                        mb: 2,
                        borderRadius: 2,
                        '&:hover': {
                          boxShadow: 3,
                          cursor: 'pointer'
                        }
                      }}
                      onClick={() => handleOpenDetailsDialog(asset)}
                    >
                      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                        <AssetImage
                          image={asset.images?.[0]}
                          alt={asset.name}
                          style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 8 }}
                        />
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="h6" sx={{ fontSize: '1.1rem', fontWeight: 600, mb: 0.5 }}>
                            {asset.name}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                            FA Number: {asset.fa_number || 'N/A'}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                            Serial Number: {asset.serial_number || 'N/A'}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                            Barcode: {asset.barcode || 'N/A'}
                          </Typography>
                          <Chip
                            label={zoneInfo?.status === 'active' ? 'Open' : 'Closed'}
                            color={zoneInfo?.status === 'active' ? 'success' : 'error'}
                            size="small"
                            sx={{ mr: 1 }}
                          />
                          <Chip
                            label={asset.location_type || 'N/A'}
                            size="small"
                            variant="outlined"
                          />
                        </Box>
                      </Box>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Typography variant="body2">
                          <strong>Zone:</strong> {zoneInfo?.name || 'N/A'}
                        </Typography>
                        <Typography variant="body2">
                          <strong>Company:</strong> {zoneInfo?.company || 'N/A'}
                        </Typography>
                        {(!zone || (zone && zone.status === 'active')) && (
                          <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleOpenDialog(asset);
                              }}
                            >
                              <EditIcon />
                            </IconButton>
                            {isAdmin && (
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDelete(asset.id);
                                }}
                              >
                                <DeleteIcon />
                              </IconButton>
                            )}
                          </Box>
                        )}
                      </Box>
                    </Paper>
                  );
                })}
                
                {/* Mobile Pagination */}
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'center', 
                  mt: 2,
                  mb: 2,
                  '& .MuiTablePagination-root': {
                    width: '100%',
                    overflow: 'hidden'
                  },
                  '& .MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': {
                    margin: 0
                  },
                  '& .MuiTablePagination-actions': {
                    marginLeft: 0
                  }
                }}>
                  <TablePagination
                    component="div"
                    count={totalCount}
                    page={page}
                    onPageChange={handleChangePage}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    labelRowsPerPage="Rows:"
                    labelDisplayedRows={({ from, to, count }) => `${from}-${to} of ${count}`}
                  />
                </Box>
              </>
            )}
          </Box>

          {/* Asset Details Dialog */}
          <Dialog 
            open={openDetailsDialog} 
            onClose={handleCloseDetailsDialog}
            maxWidth="md"
            fullWidth
            PaperProps={{
              sx: {
                borderRadius: 2,
                boxShadow: 3
              }
            }}
          >
            {selectedAsset && (
              <>
                <DialogTitle sx={{ 
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  pb: 2,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  <Box>
                    <Typography variant="h5" component="div" gutterBottom>
                      {selectedAsset.name}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                      <Chip
                        label={getZoneInfo(selectedAsset.zone_id)?.status === 'active' ? 'Open Zone' : 'Closed Zone'}
                        color={getZoneInfo(selectedAsset.zone_id)?.status === 'active' ? 'success' : 'error'}
                        size="small"
                      />
                      <Typography variant="body2" color="text.secondary">
                        Location Type: {selectedAsset.location_type || 'N/A'}
                      </Typography>
                    </Box>
                  </Box>
                  <IconButton 
                    onClick={handleCloseDetailsDialog}
                    sx={{ 
                      '&:hover': { 
                        backgroundColor: 'action.hover',
                        transform: 'rotate(90deg)',
                        transition: 'transform 0.2s'
                      }
                    }}
                  >
                    <CloseIcon />
                  </IconButton>
                </DialogTitle>
                <DialogContent sx={{ mt: 2 }}>
                  <Grid container spacing={3}>
                    {/* Main Info Section */}
                    <Grid item xs={12}>
                      <Paper 
                        elevation={0} 
                        sx={{ 
                          p: 2, 
                          backgroundColor: 'background.default',
                          borderRadius: 2
                        }}
                      >
                        <Typography variant="h6" gutterBottom sx={{ color: 'primary.main' }}>
                          Asset Information
                        </Typography>
                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={6} md={4}>
                            <Typography variant="subtitle2" color="text.secondary">FA Number</Typography>
                            <Typography variant="body1">{selectedAsset.fa_number || 'N/A'}</Typography>
                          </Grid>
                          <Grid item xs={12} sm={6} md={4}>
                            <Typography variant="subtitle2" color="text.secondary">Serial Number</Typography>
                            <Typography variant="body1">{selectedAsset.serial_number || 'N/A'}</Typography>
                          </Grid>
                          <Grid item xs={12} sm={6} md={4}>
                            <Typography variant="subtitle2" color="text.secondary">Barcode</Typography>
                            <Typography variant="body1">{selectedAsset.barcode || 'N/A'}</Typography>
                          </Grid>
                          <Grid item xs={12} sm={6} md={4}>
                            <Typography variant="subtitle2" color="text.secondary">Category</Typography>
                            <Typography variant="body1">{selectedAsset.category || 'N/A'}</Typography>
                          </Grid>
                          <Grid item xs={12} sm={6} md={4}>
                            <Typography variant="subtitle2" color="text.secondary">Quantity</Typography>
                            <Typography variant="body1">{selectedAsset.quantity || '0'}</Typography>
                          </Grid>
                          <Grid item xs={12} sm={6} md={4}>
                            <Typography variant="subtitle2" color="text.secondary">Unit</Typography>
                            <Typography variant="body1">{selectedAsset.unit || 'N/A'}</Typography>
                          </Grid>
                          <Grid item xs={12} sm={6} md={4}>
                            <Typography variant="subtitle2" color="text.secondary">Supplier</Typography>
                            <Typography variant="body1">{selectedAsset.supplier || 'N/A'}</Typography>
                          </Grid>
                          <Grid item xs={12} sm={6} md={4}>
                            <Typography variant="subtitle2" color="text.secondary">Condition</Typography>
                            <Typography variant="body1">{selectedAsset.condition || 'N/A'}</Typography>
                          </Grid>
                        </Grid>
                      </Paper>
                    </Grid>

                    {/* Location Info Section */}
                    <Grid item xs={12}>
                      <Paper 
                        elevation={0} 
                        sx={{ 
                          p: 2, 
                          backgroundColor: 'background.default',
                          borderRadius: 2
                        }}
                      >
                        <Typography variant="h6" gutterBottom sx={{ color: 'primary.main' }}>
                          Location Information
                        </Typography>
                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={6}>
                            <Typography variant="subtitle2" color="text.secondary">Zone</Typography>
                            <Typography variant="body1">
                              {getZoneInfo(selectedAsset.zone_id)?.name || 'N/A'}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Typography variant="subtitle2" color="text.secondary">Company</Typography>
                            <Typography variant="body1">
                              {getZoneInfo(selectedAsset.zone_id)?.company || 'N/A'}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Typography variant="subtitle2" color="text.secondary">Location Type</Typography>
                            <Typography variant="body1">{selectedAsset.location_type || 'N/A'}</Typography>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Typography variant="subtitle2" color="text.secondary">Zone Status</Typography>
                            <Chip
                              label={getZoneInfo(selectedAsset.zone_id)?.status === 'active' ? 'Open' : 'Closed'}
                              color={getZoneInfo(selectedAsset.zone_id)?.status === 'active' ? 'success' : 'error'}
                              size="small"
                            />
                          </Grid>
                        </Grid>
                      </Paper>
                    </Grid>

                    {/* Description Section */}
                    <Grid item xs={12}>
                      <Paper 
                        elevation={0} 
                        sx={{ 
                          p: 2, 
                          backgroundColor: 'background.default',
                          borderRadius: 2
                        }}
                      >
                        <Typography variant="h6" gutterBottom sx={{ color: 'primary.main' }}>
                          Description
                        </Typography>
                        <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                          {selectedAsset.description || 'No description available'}
                        </Typography>
                      </Paper>
                    </Grid>

                    {/* Images Section */}
                    {selectedAsset.images && selectedAsset.images.length > 0 && (
                      <Grid item xs={12}>
                        <Paper 
                          elevation={0} 
                          sx={{ 
                            p: 2, 
                            backgroundColor: 'background.default',
                            borderRadius: 2
                          }}
                        >
                          <Typography variant="h6" gutterBottom sx={{ color: 'primary.main' }}>
                            Images
                          </Typography>
                          <ImageList 
                            cols={{ xs: 1, sm: 2, md: 3 }} 
                            gap={16}
                            sx={{
                              width: '100%',
                              height: 'auto',
                              '& .MuiImageListItem-root': {
                                borderRadius: 2,
                                overflow: 'hidden',
                                '&:hover': {
                                  transform: 'scale(1.02)',
                                  transition: 'transform 0.2s',
                                  cursor: 'pointer'
                                }
                              }
                            }}
                          >
                            {selectedAsset.images.map((image, index) => (
                              <ImageListItem 
                                key={index}
                                onClick={() => window.open(`${API_BASE_URL.replace('/api', '')}/uploads/assets/${image}`, '_blank')}
                                sx={{
                                  height: { xs: 250, sm: 200, md: 200 },
                                  '& img': {
                                    height: '100%',
                                    objectFit: 'cover'
                                  }
                                }}
                              >
                                <AssetImage
                                  image={image}
                                  alt={`Asset ${index + 1}`}
                                  style={{ 
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'cover',
                                    borderRadius: 8
                                  }}
                                  fallbackSrc="https://via.placeholder.com/300"
                                />
                              </ImageListItem>
                            ))}
                          </ImageList>
                        </Paper>
                      </Grid>
                    )}
                  </Grid>
                </DialogContent>
                <DialogActions sx={{ 
                  borderTop: '1px solid',
                  borderColor: 'divider',
                  px: 3,
                  py: 2
                }}>
                  <Button 
                    onClick={handleCloseDetailsDialog}
                    variant="contained"
                    color="primary"
                  >
                    Close
                  </Button>
                </DialogActions>
              </>
            )}
          </Dialog>

          {/* Asset Creation/Edit Dialog */}
          <Dialog 
            open={openDialog} 
            onClose={handleCloseDialog}
            maxWidth="md"
            fullWidth
          >
            <DialogTitle>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                {isEditing ? 'Edit Asset' : 'Add New Asset'}
                <IconButton onClick={handleCloseDialog} size="small">
                  <CloseIcon />
                </IconButton>
              </Box>
            </DialogTitle>
            <DialogContent>
              <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Asset Name"
                      name="name"
                      id="asset-name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      margin="normal"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="FA Number"
                      name="fa_number"
                      id="asset-fa-number"
                      value={formData.fa_number}
                      onChange={handleInputChange}
                      margin="normal"
                      helperText="Optional"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Barcode"
                      name="barcode"
                      id="asset-barcode"
                      value={formData.barcode}
                      onChange={handleInputChange}
                      margin="normal"
                      helperText="Optional"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Serial Number"
                      name="serial_number"
                      id="asset-serial-number"
                      value={formData.serial_number}
                      onChange={handleInputChange}
                      margin="normal"
                      helperText="Optional"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Supplier"
                      name="supplier"
                      id="asset-supplier"
                      value={formData.supplier}
                      onChange={handleInputChange}
                      margin="normal"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Category"
                      name="category"
                      id="asset-category"
                      value={formData.category}
                      onChange={handleInputChange}
                      margin="normal"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Quantity"
                      name="quantity"
                      id="asset-quantity"
                      type="number"
                      value={formData.quantity}
                      onChange={handleInputChange}
                      margin="normal"
                      inputProps={{ min: 0 }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Unit"
                      name="unit"
                      id="asset-unit"
                      value={formData.unit}
                      onChange={handleInputChange}
                      margin="normal"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Description"
                      name="description"
                      id="asset-description"
                      value={formData.description}
                      onChange={handleInputChange}
                      multiline
                      rows={3}
                      margin="normal"
                    />
                  </Grid>
                  {(!zone || isEditing) && (
                    <>
                      <Grid item xs={12} sm={6}>
                        <FormControl fullWidth required>
                          <InputLabel id="company-select-label">Company</InputLabel>
                          <Select
                            name="company_id"
                            id="company-select"
                            value={formData.company_id}
                            onChange={handleInputChange}
                            label="Company"
                            labelId="company-select-label"
                            disabled={isEditing}
                          >
                            {companies.map((company) => (
                              <MenuItem key={company.id} value={company.id}>
                                {company.name}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <FormControl fullWidth required>
                          <InputLabel id="zone-select-label">Zone</InputLabel>
                          <Select
                            name="zone_id"
                            id="zone-select"
                            value={formData.zone_id}
                            onChange={handleInputChange}
                            label="Zone"
                            labelId="zone-select-label"
                            disabled={!formData.company_id || isEditing}
                          >
                            <MenuItem value="">
                              <em>Select a company first</em>
                            </MenuItem>
                            {zones
                              .filter(zone => zone.company_id === formData.company_id)
                              .map((zone) => (
                                <MenuItem 
                                  key={zone.id} 
                                  value={zone.id}
                                  disabled={zone.status === 'closed'}
                                >
                                  {zone.name} ({zone.status === 'active' ? 'Open' : 'Closed'})
                                </MenuItem>
                              ))}
                          </Select>
                        </FormControl>
                      </Grid>
                    </>
                  )}
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth required>
                      <InputLabel id="condition-select-label">Condition</InputLabel>
                      <Select
                        labelId="condition-select-label"
                        id="condition-select"
                        name="condition"
                        value={formData.condition || ''}
                        label="Condition"
                        onChange={handleInputChange}
                      >
                        <MenuItem value="Damaged">Damaged</MenuItem>
                        <MenuItem value="Poor">Poor</MenuItem>
                        <MenuItem value="Good">Good</MenuItem>
                        <MenuItem value="Out Of Use">Out Of Use</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                      {/* PC Upload Button */}
                      <input
                        accept="image/*"
                        type="file"
                        multiple
                        onChange={handleImageChange}
                        style={{ display: 'none' }}
                        id="pc-image-upload"
                        name="images"
                      />
                      <label htmlFor="pc-image-upload">
                        <Button
                          variant="outlined"
                          component="span"
                          startIcon={<PhotoCameraIcon />}
                          disabled={selectedImages.length >= 5}
                        >
                          Upload from PC
                        </Button>
                      </label>

                      {/* Mobile Upload Button */}
                      <input
                        accept="image/*"
                        type="file"
                        multiple
                        onChange={handleImageChange}
                        style={{ display: 'none' }}
                        id="mobile-image-upload"
                        name="images"
                        capture="environment"
                      />
                      <label htmlFor="mobile-image-upload">
                        <Button
                          variant="outlined"
                          component="span"
                          startIcon={<PhotoCameraIcon />}
                          disabled={selectedImages.length >= 5}
                        >
                          Upload from Mobile
                        </Button>
                      </label>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                      {imagePreviews.map((preview, index) => (
                        <Box key={index} position="relative">
                          <img
                            src={preview.preview}
                            alt={`Preview ${index + 1}`}
                            style={{ 
                              width: 100, 
                              height: 100, 
                              objectFit: 'cover', 
                              borderRadius: 4,
                              border: preview.isExisting ? '2px solid #4caf50' : 'none'
                            }}
                          />
                          {preview.isExisting && (
                            <Typography 
                              variant="caption" 
                              sx={{ 
                                position: 'absolute', 
                                bottom: 0, 
                                left: 0, 
                                right: 0, 
                                bgcolor: 'rgba(0,0,0,0.5)', 
                                color: 'white',
                                textAlign: 'center',
                                fontSize: '0.7rem'
                              }}
                            >
                              Existing
                            </Typography>
                          )}
                          {uploadProgress[index] !== undefined && (
                            <Box sx={{ width: '100%', mt: 1 }}>
                              <LinearProgress 
                                variant="determinate" 
                                value={uploadProgress[index]} 
                                sx={{ height: 4, borderRadius: 2 }}
                              />
                              <Typography variant="caption" sx={{ display: 'block', textAlign: 'center' }}>
                                {uploadProgress[index]}%
                              </Typography>
                            </Box>
                          )}
                          <IconButton
                            size="small"
                            onClick={() => removeImage(index)}
                            sx={{
                              position: 'absolute',
                              top: 0,
                              right: 0,
                              backgroundColor: 'rgba(255, 255, 255, 0.8)',
                              '&:hover': {
                                backgroundColor: 'rgba(255, 0, 0, 0.1)'
                              }
                            }}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Box>
                      ))}
                      {compressing && (
                        <Box sx={{ width: '100%', mt: 2 }}>
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            Compressing images...
                          </Typography>
                          <LinearProgress />
                        </Box>
                      )}
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseDialog}>Cancel</Button>
              {(!zone || (zone && zone.status === 'active')) && (
                <Button
                  onClick={handleSubmit}
                  variant="contained"
                  disabled={!formData.name || (!zone && !formData.zone_id)}
                >
                  {isEditing ? 'Update' : 'Create'}
                </Button>
              )}
            </DialogActions>
          </Dialog>
        </Paper>
      </Container>
    </Box>
  );
}

const AssetImage = React.memo(({ image, alt, style, fallbackSrc = 'https://via.placeholder.com/50' }) => {
  const [imgError, setImgError] = useState(false);
  const [imgLoading, setImgLoading] = useState(true);
  const [attemptedUrl, setAttemptedUrl] = useState('');
  const imgRef = useRef(null);

  // Construct the image URL only once and memoize it
  const imageUrl = useMemo(() => {
    if (!image) return '';
    
    // Handle blob images differently
    if (image.includes('_blob')) {
      // For blob images, we need to use the uploads directory directly
      return `${API_BASE_URL.replace('/api', '')}/uploads/assets/${image}`;
    }
    
    // For regular images, use the get_image.php endpoint
    const cacheKey = Math.floor(Date.now() / (60 * 60 * 1000));
    return `${API_BASE_URL}/assets/get_image.php?path=${encodeURIComponent(image)}&_=${cacheKey}`;
  }, [image]);

  // Use Intersection Observer to load images only when they're visible
  useEffect(() => {
    if (!image || !imgRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting && imgRef.current) {
            // Only load the image if it's visible and hasn't been attempted
            if (!attemptedUrl) {
              setAttemptedUrl(imageUrl);
            }
            observer.unobserve(entry.target);
          }
        });
      },
      {
        rootMargin: '50px 0px', // Start loading when image is 50px from viewport
        threshold: 0.1
      }
    );

    observer.observe(imgRef.current);
    return () => observer.disconnect();
  }, [image, imageUrl, attemptedUrl]);

  const handleImageError = (e) => {
    if (imgError) return; // Prevent multiple error logs
    
    // Try alternative URL if the first attempt fails
    if (!imgError && attemptedUrl === imageUrl) {
      // If the first attempt was using get_image.php, try direct uploads path
      if (imageUrl.includes('get_image.php')) {
        const directUrl = `${API_BASE_URL.replace('/api', '')}/uploads/assets/${image}`;
        setAttemptedUrl(directUrl);
        return;
      }
      // If the first attempt was direct path, try get_image.php
      else {
        const apiUrl = `${API_BASE_URL}/assets/get_image.php?path=${encodeURIComponent(image)}`;
        setAttemptedUrl(apiUrl);
        return;
      }
    }
    
    console.error('Image load error:', {
      image,
      attemptedUrl,
      finalError: true
    });
    setImgError(true);
    setImgLoading(false);
  };

  if (!image) {
    return (
      <Box sx={{ ...style, bgcolor: 'grey.200', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Typography variant="body2" color="text.secondary">No image</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ position: 'relative', ...style }} ref={imgRef}>
      {imgLoading && (
        <Box sx={{ 
          position: 'absolute', 
          top: 0, 
          left: 0, 
          right: 0, 
          bottom: 0, 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          bgcolor: 'grey.100'
        }}>
          <CircularProgress size={20} />
        </Box>
      )}
      {attemptedUrl && (
        <img
          src={imgError ? fallbackSrc : attemptedUrl}
          alt={alt}
          style={{ 
            ...style,
            opacity: imgLoading ? 0 : 1,
            transition: 'opacity 0.3s'
          }}
          onLoad={() => {
            setImgLoading(false);
          }}
          onError={handleImageError}
          loading="lazy" // Enable native lazy loading
        />
      )}
    </Box>
  );
});

export default Assets; 